import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import { cn } from '@/libs/utils';

function Dialog({
  open,
  title,
  children,
  triggerChildren,
  confirmLoading,
  onCancel,
  form,
  className,
  cancelText = 'cancel',
  okText = 'confirm',
  okButtonClassName,
  cancelButtonClassName,
  maskClosable = true,
  destroyOnHidden = false,
  hasFooter = true,
  okButtonProps,
  cancelButtonProps,
  closable = true,
  autoFocusButton = null,
  // antd writes its own `width` as an inline style on `.ant-modal`, so a
  // Tailwind width class on `className` never wins and a `max-w-` one cannot
  // widen the box at all. A form laid out in two columns needs the real prop.
  width,
}) {
  const { t } = useTranslation('forms');

  useEffect(() => {
    if (open && autoFocusButton === 'ok') {
      // Small delay to ensure modal is fully rendered
      const timer = setTimeout(() => {
        const okButton = document.querySelector('.ant-modal-footer .ant-btn-primary');
        if (okButton) {
          okButton.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, autoFocusButton]);

  return (
    <>
      {triggerChildren}

      <Modal
        title={title}
        open={open}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
        onCancel={onCancel}
        okText={t(okText)}
        cancelText={t(cancelText)}
        closable={closable}
        okButtonProps={{
          htmlType: 'submit',
          className: cn(
            'btn-danger inline-flex',
            'focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:outline-none',
            okButtonClassName
          ),
          ...okButtonProps,
        }}
        cancelButtonProps={{
          className: cn(
            'btn-default btn-default--hover inline-flex',
            cancelButtonClassName
          ),
          ...cancelButtonProps,
        }}
        className={cn(
          'ant-modal-wrapper',
          className,
          !hasFooter ? 'custom-ant-modal-body' : ''
        )}
        maskClosable={maskClosable}
        {...(width !== undefined ? { width } : {})}
        destroyOnHidden={destroyOnHidden}
        footer={!hasFooter ? true : undefined}
      >
        {children}
      </Modal>
    </>
  );
}

function DialogTitle({ className, text, dynamicText }) {
  return (
    <h3
      className={cn(
        'flex flex-wrap items-center justify-center text-center text-base font-light leading-8',
        className
      )}
    >
      <span>{text}</span>

      {dynamicText && (
        <>
          {''}
          <strong className="ml-2 font-semibold leading-6">
            {dynamicText}
            <span>?</span>
          </strong>
        </>
      )}
    </h3>
  );
}

export { Dialog, DialogTitle };
